/** The members of a channel (channel details → Members): search, add people, remove, badges and sponsors. */
import type { ChannelMembersState, ChannelMembersViewModel, ChannelMemberView } from "@bundle/core";
import { Avatar, Button, Chip, Icon, Pressable, Stack, Text, TextField, Toast } from "../primitives/index.ts";
import { useT, useTr } from "../i18n/I18nProvider.tsx";
import { AgentBadge, ExternalBadge } from "./Badges.tsx";
import { StatusEmoji } from "./StatusEmoji.tsx";

function RoleBadge({ role }: { role: NonNullable<ChannelMemberView["role"]> }) {
  const t = useT();
  return <Chip label={role === "owner" ? t("channelMembers.role.owner") : role === "admin" ? t("channelMembers.role.admin") : t("channelMembers.role.guest")} />;
}

function MemberRow({ row, onOpen, onRemove }: { row: ChannelMemberView; onOpen: () => void; onRemove: () => void }) {
  const t = useT();
  const secondary = [row.handle === undefined ? undefined : `@${row.handle}`, row.fullName].filter((x) => x !== undefined).join(" · ");
  return (
    <Stack direction="horizontal" align="center" gap="2" hoverGroup testID={`member-${row.userId}`}>
      <Pressable direction="horizontal" radius="md" onPress={onOpen} accessibilityLabel={row.name} testID={`member-open-${row.userId}`}>
        <Stack grow direction="horizontal" align="center" gap="2.5" paddingX="2" paddingY="1.5">
          <Avatar name={row.name} src={row.avatarUrl} presence={row.presence} dnd={row.dnd} dndLabel={t("dnd.paused")} size="avatar.sm" square={row.isAgent} />
          <Stack grow gap="0.5">
            <Stack direction="horizontal" align="center" gap="1.5" wrap>
              <Text weight="medium" lines={1}>
                {row.name}
              </Text>
              <StatusEmoji person={row} testID={`member-status-${row.userId}`} />
              {row.isMe ? (
                <Text variant="footnote" color="text.tertiary">
                  {t("channelMembers.you")}
                </Text>
              ) : null}
              {row.role === undefined ? null : <RoleBadge role={row.role} />}
              {row.isAgent ? <AgentBadge /> : null}
              {row.organization === undefined ? null : <ExternalBadge organization={row.organization} />}
            </Stack>
            {secondary === "" ? null : (
              <Text variant="footnote" color="text.secondary" lines={1}>
                {secondary}
              </Text>
            )}
            {row.sponsorName === undefined ? null : (
              <Text variant="caption" color="text.tertiary" lines={1}>
                {t("channelMembers.sponsor", { name: row.sponsorName })}
              </Text>
            )}
          </Stack>
        </Stack>
      </Pressable>
      {row.canRemove ? (
        <Stack revealOnHover>
          <Button label={t("channelMembers.remove")} size="sm" variant="ghost" onPress={onRemove} testID={`member-remove-${row.userId}`} />
        </Stack>
      ) : null}
    </Stack>
  );
}

function AddPeople({ vm, state: s }: { vm: ChannelMembersViewModel; state: ChannelMembersState }) {
  const t = useT();
  return (
    <Stack gap="2" testID="members-add">
      {s.picked.length === 0 ? null : (
        <Stack direction="horizontal" gap="1.5" wrap>
          {s.picked.map((m) => (
            <Pressable key={m.userId} radius="full" onPress={() => vm.unpick(m.userId)} accessibilityLabel={t("channels.create.removeMember", { name: m.name })} testID={`members-picked-${m.userId}`}>
              <Stack direction="horizontal" align="center" gap="1.5" paddingX="2" paddingY="1" radius="full" background="chip.bg">
                <Avatar name={m.name} src={m.avatarUrl} size="avatar.xs" square={m.isAgent} />
                <Text variant="footnote" color="chip.fg">
                  {m.name}
                </Text>
                <Icon name="close" size="icon.sm" color="text.tertiary" />
              </Stack>
            </Pressable>
          ))}
        </Stack>
      )}
      <TextField value={s.addQuery} onChange={(v) => vm.setAddQuery(v)} placeholder={t("channelMembers.addSearch")} leadingIcon="search" autoFocus testID="members-add-search" />
      <Stack gap="0.5">
        {s.candidates.length === 0 ? (
          <Text variant="footnote" color="text.tertiary">
            {t("channelMembers.addNone")}
          </Text>
        ) : (
          s.candidates.map((m) => (
            <Pressable key={m.userId} direction="horizontal" radius="md" onPress={() => vm.pick(m.userId)} testID={`members-candidate-${m.userId}`}>
              <Stack grow direction="horizontal" align="center" gap="2" paddingX="2" paddingY="1">
                <Avatar name={m.name} src={m.avatarUrl} size="avatar.xs" square={m.isAgent} />
                <Stack grow>
                  <Text variant="footnote" lines={1}>
                    {m.name}
                  </Text>
                </Stack>
                {m.organization === undefined ? null : (
                  <Text variant="caption" color="external.fg" lines={1}>
                    {m.organization}
                  </Text>
                )}
                <Icon name="plus" size="icon.sm" color="text.tertiary" />
              </Stack>
            </Pressable>
          ))
        )}
      </Stack>
      <Stack direction="horizontal" align="center" justify="end" gap="2">
        <Button label={t("common.cancel")} variant="ghost" onPress={() => vm.cancelAdding()} testID="members-add-cancel" />
        <Button
          label={t("channelMembers.addSubmit", { count: s.picked.length })}
          variant="primary"
          icon="plus"
          loading={s.busy}
          disabled={s.picked.length === 0}
          onPress={() => void vm.addPicked()}
          testID="members-add-submit"
        />
      </Stack>
    </Stack>
  );
}

/** The members list (Slack's channel details → Members): add people, search, the list, remove; guests' organization and sponsor. */
export function ChannelMembersList({ vm, state: s, onOpenMember }: { vm: ChannelMembersViewModel; state: ChannelMembersState; onOpenMember: (userId: string) => void }) {
  const t = useT();
  const tr = useTr();
  return (
    <Stack gap="3" testID="channel-members-list">
      {s.adding ? (
        <AddPeople vm={vm} state={s} />
      ) : s.canAdd ? (
        <Stack direction="horizontal">
          <Button label={t("channelMembers.add")} icon="invite" size="sm" onPress={() => vm.startAdding()} testID="members-add-open" />
        </Stack>
      ) : null}
      {s.isDefault ? (
        <Text variant="footnote" color="text.secondary">
          {t("channelMembers.everyone")}
        </Text>
      ) : null}
      <TextField value={s.query} onChange={(v) => vm.setQuery(v)} placeholder={t("channelMembers.search")} leadingIcon="search" testID="members-search" />
      {s.status === "loading" ? (
        <Text color="text.tertiary">{t("common.loading")}</Text>
      ) : s.status === "offline" ? (
        <Text color="text.tertiary">{t("channelMembers.offline")}</Text>
      ) : s.status === "error" ? (
        <Stack direction="horizontal" align="center" gap="2">
          <Text color="status.danger">{t("channelMembers.failed")}</Text>
          <Button label={t("common.retry")} size="sm" onPress={() => void vm.load()} testID="members-retry" />
        </Stack>
      ) : s.rows.length === 0 ? (
        <Text color="text.tertiary">{t("channelMembers.empty")}</Text>
      ) : (
        <Stack gap="0.5" testID="members-list">
          {s.rows.map((row) => (
            <MemberRow key={row.userId} row={row} onOpen={() => onOpenMember(row.userId)} onRemove={() => void vm.remove(row.userId)} />
          ))}
        </Stack>
      )}
      <Toast open={s.notice !== undefined} onClose={() => vm.dismissNotice()} message={s.notice === undefined ? "" : tr(s.notice)} closeLabel={t("common.close")} durationMs={4000} testID="members-notice" />
    </Stack>
  );
}
